// src/pages/comercial/AnaliseVendas.js
import React, { useEffect, useMemo, useState } from "react";

// API e contexto
import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

// react-table
import { useTable, useFilters, useSortBy } from "react-table";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Charts (igual Dashboard / MenuSearchMateriais)
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

// MUI
import {
  Card,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Checkbox,
  Box as MuiBox,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Layout JFB
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// PageHeader (título/subtítulo vêm de routes.js)
import PageHeader from "pages/components/PageHeader";

const TABLE_KEY = "analise-vendas";
const LS_FILTERS_KEY = `tbl:${TABLE_KEY}:filters`;
const LS_KEEP_KEY = `tbl:${TABLE_KEY}:keep`;

// Filtro padrão para react-table (igual AnaliseStocks)
function DefaultColumnFilter({ column }) {
  const { filterValue, setFilter } = column;
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        padding: "2px 4px",
        fontSize: 11,
        border: "none",
        borderBottom: "1px solid #e0e0e0",
        outline: "none",
        backgroundColor: "transparent",
      }}
      placeholder="Filtrar."
    />
  );
}

const MESES = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const MESES_LABELS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const DIAS = Array.from({ length: 31 }, (_, i) => i + 1);

const fmt = new Intl.NumberFormat("pt-PT", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const METRIC_CONFIG = {
  eur: {
    title: "Vendas mensais (sem IVA, sem vasilhame)",
    caption: "Totais anuais indicados na legenda. Valores em euros.",
    suffix: " €",
  },
  litros: {
    title: "Litros faturados mensalmente",
    caption: "Totais anuais indicados na legenda. Litros.",
    suffix: "",
  },
  pesoBru: {
    title: "Peso bruto mensal faturado",
    caption: "Totais anuais indicados na legenda. Peso bruto.",
    suffix: "",
  },
  qtd: {
    title: "Quantidades faturadas mensalmente",
    caption: "Totais anuais indicados na legenda. Quantidades (qtd).",
    suffix: "",
  },
  clientes: {
    title: "Clientes distintos (mensal)",
    caption: "Número de clientes diferentes com vendas no período.",
    suffix: "",
  },
};

function AnaliseVendas() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();
  const theme = useTheme();

  const isAdminLike = useMemo(
    () => temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria"),
    [temFuncao]
  );
  const isComercial = useMemo(() => temFuncao("Comercial"), [temFuncao]);
  const refUser = useMemo(
    () => (userData?.refvendedor ? String(userData.refvendedor) : ""),
    [userData]
  );

  // quando Comercial e não administrador, este vendedor é obrigatório
  const vendedorForcado = !isAdminLike && isComercial && refUser ? refUser : "";

  const fmt = useMemo(
    () =>
      new Intl.NumberFormat("pt-PT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const colorMap = {
    warning: theme.palette.warning.main,
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    info: theme.palette.info.main, // ✅ para a linha Compras
  };

  const [loading, setLoading] = useState(false);

  // ----------------- Filtros -----------------
  const anoAtual = new Date().getFullYear();
  const anosOptions = useMemo(() => {
    const anos = [];
    for (let a = anoAtual; a >= 2023; a--) anos.push(a);
    return anos;
  }, [anoAtual]);

  const [anosSel, setAnosSel] = useState([anoAtual, anoAtual - 1, anoAtual - 2]);

  //const mesAtual = new Date().getMonth() + 1;

  const [mesIni, setMesIni] = useState(1);
  const [mesFim, setMesFim] = useState(12);

  const [diaIni, setDiaIni] = useState(1);
  const [diaFim, setDiaFim] = useState(31);

  const [vendedores, setVendedores] = useState([]);
  // Guarda apenas os refs selecionados
  const [vendedoresSel, setVendedoresSel] = useState([]);

  const [todosVendedores, setTodosVendedores] = useState(true);

  const [fabricantes, setFabricantes] = useState([]);
  const [fabricanteSel, setFabricanteSel] = useState(null);

  const [clientes, setClientes] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);

  const [familias, setFamilias] = useState([]);
  const [familiasSel, setFamiliasSel] = useState([]); // Guarda apenas os refs selecionados

  const [materiais, setMateriais] = useState([]);
  const [materiaisSel, setMateriaisSel] = useState([]); // Guarda apenas os refs selecionados
  const [materiaisDisabled, setMateriaisDisabled] = useState(true); // controla se o accordion deve estar desativado (por regra de famílias)
  const [materiaisLoadedKey, setMateriaisLoadedKey] = useState(""); // evita refazer pedidos iguais
  const [materiaisLoading, setMateriaisLoading] = useState(false);
  const [materiaisFiltro, setMateriaisFiltro] = useState("");

  const [embalagens, setEmbalagens] = useState([]);
  const [embalagensSel, setEmbalagensSel] = useState([]);

  const [vendedoresVisiveis, setVendedoresVisiveis] = useState([]);
  const [familiasVisiveis, setFamiliasVisiveis] = useState([]);

  const [todasFamilias, setTodasFamilias] = useState(true);

  const [nomeFiltroModo, setNomeFiltroModo] = useState("contém"); // "contém" | "naoContém"
  const [nomeFiltroTexto, setNomeFiltroTexto] = useState("");

  const [imgsByRef, setImgsByRef] = useState({});

  // Apenas um accordion aberto de cada vez
  const [expandedPanel, setExpandedPanel] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
  };

  // ----------------- Dados -----------------
  const [resumoMensal, setResumoMensal] = useState([]); // para gráfico
  const [resumoMensalCompras, setResumoMensalCompras] = useState([]); // para gráfico (compras)

  const [rows, setRows] = useState([]); // para tabela

  const [clientesPorVendedor, setClientesPorVendedor] = useState([]); // só para métrica "clientes"

  const [keepFilters, setKeepFilters] = useState(() => localStorage.getItem(LS_KEEP_KEY) !== "0");

  const [metric, setMetric] = useState("eur");

  const savedFilters = useMemo(() => {
    try {
      const raw = localStorage.getItem(LS_FILTERS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  // ============================================================
  // Combos iniciais
  // ============================================================
  useEffect(() => {
    const carregarCombos = async () => {
      setLoading(true);
      try {
        // Vendedores
        {
          const [dados, err] = await handleApi(api.post("Comercial/SelVendedoresCombo", {}));
          if (err) {
            showAlert(err, "error");
          } else {
            const lista = Array.isArray(dados) ? dados : [];

            if (vendedorForcado) {
              // Comercial: só vê o próprio vendedor, já selecionado
              const apenasMeu = lista.filter((v) => String(v.ref) === vendedorForcado);
              setVendedores(apenasMeu);
              setVendedoresVisiveis(apenasMeu);
              setVendedoresSel(apenasMeu.map((v) => v.ref));
              setTodosVendedores(false);
            } else {
              // restantes funções: todos selecionados por defeito
              setVendedores(lista);
              setVendedoresVisiveis(lista);
              setVendedoresSel(lista.map((v) => v.ref));
              setTodosVendedores(true);
            }
          }
        }

        // Fabricantes
        {
          const [dados, err] = await handleApi(api.post("Comercial/SelFabricantesCombo", {}));
          if (err) {
            showAlert(err, "error");
          } else {
            const lista = Array.isArray(dados) ? dados : [];
            const todos = { ref: "", nome: "TODOS" };
            const final = [todos, ...lista];
            setFabricantes(final);
            setFabricanteSel(todos);
          }
        }

        // Clientes
        {
          const bodyClientes = {
            refvendedor: vendedorForcado || "",
            funcoes: userData?.funcoes || [],
          };

          const [dados, err] = await handleApi(
            api.post("Comercial/SelClientesCombo", bodyClientes)
          );

          if (err) {
            showAlert(err, "error");
          } else {
            const lista = Array.isArray(dados) ? dados : [];
            const todos = { ref: "", nome: "TODOS" }; // sempre "todos" mas só dentro do universo permitido
            const final = [todos, ...lista];
            setClientes(final);
            setClienteSel(todos);
          }
        }

        // Famílias
        {
          const [dados, err] = await handleApi(api.post("Comercial/SelFamiliasCombo", {}));
          if (err) {
            showAlert(err, "error");
          } else {
            const lista = Array.isArray(dados) ? dados : [];
            setFamilias(lista);
            setFamiliasVisiveis(lista);
            // guardar APENAS as refs das famílias selecionadas
            setFamiliasSel(lista.map((f) => f.ref));
          }
        }

        // Embalagens
        {
          const [dados, err] = await handleApi(api.post("Comercial/SelEmbalagensCombo", {}));
          if (err) {
            showAlert(err, "error");
          } else {
            const lista = Array.isArray(dados) ? dados : [];
            setEmbalagens(lista);
            setEmbalagensSel(lista.map((e) => e.ref));
          }
        }
      } catch {
        showAlert("Erro ao carregar dados auxiliares.", "error");
      } finally {
        setLoading(false);
      }
    };

    carregarCombos();
  }, [showAlert, vendedorForcado]);

  const carregarImagensMateriais = async (refs) => {
    const lista = Array.from(
      new Set((refs || []).map((r) => String(r || "").trim()).filter(Boolean))
    );

    // pedir apenas as que ainda não temos
    const faltam = lista.filter((r) => !(imgsByRef || {})[r]);

    if (faltam.length === 0) return;

    const [dados, err] = await handleApi(
      api.post("Materiais/SelMateriaisImagens", { refs: faltam })
    );

    if (err) return;

    const mapa = {};
    (Array.isArray(dados) ? dados : []).forEach((x) => {
      const ref = String(x?.refmaterial || "").trim();
      const url = String(x?.url || "").trim();
      if (ref && url) mapa[ref] = url;
    });

    setImgsByRef((prev) => ({ ...(prev || {}), ...mapa }));
  };

  // ============================================================
  // Carregar análise de vendas
  // ============================================================
  const carregarDados = async (override = {}) => {
    setLoading(true);

    try {
      const vendSel = override.vendedoresSel ?? vendedoresSel;
      const famSel = override.familiasSel ?? familiasSel;
      const matSel = override.materiaisSel ?? materiaisSel;
      const embSel = override.embalagensSel ?? embalagensSel;
      const fromManual = override.fromManual || false;

      // se houver vendedor forçado (Comercial), ignora seleção manual
      let vendedoresApi;
      if (vendedorForcado) {
        vendedoresApi = [vendedorForcado];
      } else {
        const isTodosVend = vendSel.length === 0 || vendSel.length === vendedores.length;
        vendedoresApi = isTodosVend ? [] : vendSel;
      }

      const isTodasFam = famSel.length === 0 || famSel.length === familias.length;

      const isTodosMat =
        materiaisDisabled || matSel.length === 0 || matSel.length === materiais.length;

      const isTodasEmb = embSel.length === 0 || embSel.length === embalagens.length;

      const body = {
        anos: Array.isArray(anosSel) ? anosSel.map((a) => Number(a)).filter(Number.isFinite) : [],
        mesInicial: mesIni,
        mesFinal: mesFim,
        diaInicial: diaIni,
        diaFinal: diaFim,
        vendedores: vendedoresApi,
        refFabricante: fabricanteSel?.ref || "",
        refCliente: clienteSel?.ref || "",
        metric: metric,
        familias: isTodasFam ? [] : famSel,
        materiais: isTodosMat ? [] : matSel,
        embalagens: isTodasEmb ? [] : embSel,
        // backend vai aplicar sempre este filtro se não for string vazia
        refvendedor: vendedorForcado || "",
        // filtro de texto no nome do material
        textoMaterial: nomeFiltroTexto.trim(),
        nomeNaoContem: nomeFiltroModo === "naoContem",
        funcoes: userData?.funcoes || [],
      };

      const [dados, err] = await handleApi(api.post("Comercial/SelAnaliseVendas", body));

      console.log("dados", dados);
      console.log("err", err);

      if (err) {
        // erro lógico da API (success = false, etc.)
        showAlert(err, "error");
        setResumoMensal([]);
        setResumoMensalCompras([]);

        setRows([]);
        setLoading(false); // defesa extra para garantir que o spinner pára
        return;
      }

      const resumo = Array.isArray(dados?.resumo) ? dados.resumo : [];
      const resumoCompras = Array.isArray(dados?.resumoCompras) ? dados.resumoCompras : [];

      const linhas = Array.isArray(dados?.linhas) ? dados.linhas : [];
      const clientesVend = Array.isArray(dados?.clientesPorVendedor)
        ? dados.clientesPorVendedor
        : [];

      // console.log("linhas", linhas);

      setResumoMensal(resumo);
      setResumoMensalCompras(resumoCompras);

      setRows(linhas);
      setClientesPorVendedor(metric === "clientes" ? clientesVend : []);

      carregarImagensMateriais(linhas.map((l) => l.refmaterial));

      // aviso explícito quando não há dados
      if (resumo.length === 0 && linhas.length === 0) {
        showAlert("Sem dados com os filtros selecionados.", "info");
      }

      //   // ------------------------------------------------------------------
      //   // Ajustar checkboxes APENAS quando o carregamento não vem de clique manual
      //   // (ex.: mudança de fabricante/cliente ou primeiro load)
      //   // ------------------------------------------------------------------
      //   if (linhas.length > 0 && !fromManual) {
      //     const refsVendUsados = new Set(
      //       linhas.map((l) => l.refvendedor).filter((v) => v != null && v !== "")
      //     );
      //     const refsFamUsadas = new Set(
      //       linhas.map((l) => l.refmaterialfamilia).filter((f) => f != null && f !== "")
      //     );
      //     const refsEmbUsadas = new Set(
      //       linhas.map((l) => l.refmaterialembalagem).filter((e) => e != null && e !== "")
      //     );

      //     const nextVendSel = Array.from(refsVendUsados);
      //     const nextFamSel = Array.from(refsFamUsadas);
      //     const nextEmbSel = Array.from(refsEmbUsadas);

      //     // só muda o state se o conteúdo for realmente diferente (evita loops)
      //     setVendedoresSel((prev) => {
      //       if (prev.length === nextVendSel.length && prev.every((v, i) => v === nextVendSel[i])) {
      //         return prev;
      //       }
      //       return nextVendSel;
      //     });

      //     setFamiliasSel((prev) => {
      //       if (prev.length === nextFamSel.length && prev.every((v, i) => v === nextFamSel[i])) {
      //         return prev;
      //       }
      //       return nextFamSel;
      //     });

      //     setEmbalagensSel((prev) => {
      //       if (prev.length === nextEmbSel.length && prev.every((v, i) => v === nextEmbSel[i])) {
      //         return prev;
      //       }
      //       return nextEmbSel;
      //     });
      //   }
    } catch (e) {
      console.error(e);
      showAlert("Erro ao carregar análise de vendas.", "error");
      setResumoMensal([]);
      setResumoMensalCompras([]);

      setRows([]);
      setClientesPorVendedor([]);
    } finally {
      // em condições normais já terá sido posto a false acima,
      // mas o finally garante que nunca fica preso em true
      setLoading(false);
    }
  };

  // Carregamento automático APENAS inicial (depois dos combos estarem prontos)
  const [jaCarregouInicial, setJaCarregouInicial] = useState(false);

  useEffect(() => {
    if (jaCarregouInicial) return;

    if (
      vendedores.length === 0 ||
      fabricantes.length === 0 ||
      clientes.length === 0 ||
      familias.length === 0 ||
      embalagens.length === 0
    ) {
      return;
    }

    carregarDados();
    setJaCarregouInicial(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jaCarregouInicial,
    vendedores.length,
    fabricantes.length,
    clientes.length,
    familias.length,
    embalagens.length,
  ]);

  // ============================================================
  // Preparar gráfico (igual ao padrão Dashboard / MenuSearchMateriais)
  // ============================================================
  const vendasChart = useMemo(() => {
    if (!Array.isArray(resumoMensal)) return null;

    // ------------------------------------------------------------
    // 1) Anos-base: vêm SEMPRE de anosSel (igual ao Dashboard)
    // ------------------------------------------------------------
    const anos =
      Array.isArray(anosSel) && anosSel.length > 0
        ? [...anosSel]
            .map(Number)
            .filter(Number.isFinite)
            .sort((a, b) => b - a)
        : [];

    if (anos.length === 0) return null;

    // ------------------------------------------------------------
    // 2) Intervalo de meses selecionado
    // ------------------------------------------------------------
    const mi = Number(mesIni); // 1..12
    const mf = Number(mesFim); // 1..12

    // ------------------------------------------------------------
    // 3) Inicialização
    //    - null fora do intervalo
    //    - 0 dentro do intervalo (para o ano aparecer mesmo sem vendas)
    // ------------------------------------------------------------
    const porAno = {};
    const totaisAno = {};

    anos.forEach((ano) => {
      porAno[ano] = new Array(12).fill(null);
      totaisAno[ano] = 0;

      for (let m = mi; m <= mf; m++) {
        porAno[ano][m - 1] = 0;
      }
    });

    // ------------------------------------------------------------
    // 4) Função de extração do valor conforme métrica
    // ------------------------------------------------------------
    const getValor = (r) => {
      if (metric === "eur") return Number(r.valortotal ?? r.valor ?? 0);
      if (metric === "litros") return Number(r.volume ?? 0);
      if (metric === "pesoBru") return Number(r.pesobruto ?? 0);
      if (metric === "qtd") return Number(r.quantidade ?? 0);
      if (metric === "clientes") return Number(r.clientes ?? 0);
      return 0;
    };

    // ------------------------------------------------------------
    // 5) Aplicar dados reais (se existirem)
    // ------------------------------------------------------------
    for (const r of resumoMensal) {
      const ano = Number(r.ano);
      const mes = Number(r.mes);

      if (!anos.includes(ano)) continue;
      if (mes < mi || mes > mf) continue;

      const valor = getValor(r);
      porAno[ano][mes - 1] += valor;
      totaisAno[ano] += valor;
    }

    // ------------------------------------------------------------
    // 6) Meses futuros do ano atual -> null (igual Dashboard)
    // ------------------------------------------------------------
    const anoAtualLocal = new Date().getFullYear();
    const mesAtualIdx = new Date().getMonth(); // 0..11

    if (porAno[anoAtualLocal]) {
      for (let m = mesAtualIdx + 1; m < 12; m++) {
        porAno[anoAtualLocal][m] = null;
      }
    }

    // ------------------------------------------------------------
    // 7) Labels (OBRIGATÓRIO para o DefaultLineChart)
    // ------------------------------------------------------------
    const labels = MESES_LABELS;

    // ------------------------------------------------------------
    // 8) Cores (iguais ao Dashboard)
    // ------------------------------------------------------------
    const anoAnterior = anoAtualLocal - 1;
    const anoAntepenultimo = anoAtualLocal - 2;

    const corPorAno = {
      [anoAtualLocal]: "warning",
      [anoAnterior]: "primary",
      [anoAntepenultimo]: "secondary",
    };

    const apenasUmAno = anos.length === 1;

    const datasetsBase = anos.map((ano) => ({
      label: apenasUmAno ? `${ano}_Vendas` : String(ano),
      data: porAno[ano],
      tension: 0.4,
      pointRadius: 3,
      borderWidth: 3,
      color: corPorAno[ano] || "info",
    }));

    const datasets = [...datasetsBase];

    // ------------------------------------------------------------
    // 9) Linha Compras (só 1 ano e só quando há fabricante selecionado)
    // ------------------------------------------------------------
    const temFabricante = Boolean((fabricanteSel?.ref || "").trim());
    const temCompras = Array.isArray(resumoMensalCompras) && resumoMensalCompras.length > 0;

    const totaisComprasAno = {};

    if (isAdminLike && apenasUmAno && temFabricante && temCompras && metric !== "clientes") {
      const ano = anos[0];

      // inicializa array compras
      const comprasPorMes = new Array(12).fill(null);
      for (let m = mi; m <= mf; m++) comprasPorMes[m - 1] = 0;

      totaisComprasAno[ano] = 0;

      for (const r of resumoMensalCompras) {
        const a = Number(r.ano);
        const mes = Number(r.mes);
        if (a !== ano) continue;
        if (mes < mi || mes > mf) continue;

        let valor = 0;
        if (metric === "eur") valor = Number(r.valortotal ?? r.valor ?? 0);
        else if (metric === "litros") valor = Number(r.volume ?? 0);
        else if (metric === "qtd") valor = Number(r.quantidade ?? 0);

        comprasPorMes[mes - 1] += valor;
        totaisComprasAno[ano] += valor;
      }

      // meses futuros do ano atual -> null (igual regra das vendas)
      if (ano === anoAtualLocal && comprasPorMes) {
        for (let m = mesAtualIdx + 1; m < 12; m++) comprasPorMes[m] = null;
      }

      datasets.push({
        label: `${ano}_Compras`,
        data: comprasPorMes,
        tension: 0.4,
        pointRadius: 3,
        borderWidth: 3,
        color: "info",
      });
    }

    return {
      labels,
      datasets,
      totaisAno,
      totaisComprasAno,
    };
  }, [
    isAdminLike,
    resumoMensal,
    resumoMensalCompras,
    anosSel,
    mesIni,
    mesFim,
    metric,
    fabricanteSel,
    imgsByRef, // ✅ manter para coerência visual
  ]);

  // ============================================================
  // Tabela
  // ============================================================
  const columns = useMemo(() => {
    const colData = {
      Header: "Data",
      accessor: "faturadata",
      Cell: ({ value }) => {
        if (!value) return "";
        const s = String(value);
        return s.length >= 10 ? s.slice(0, 10) : s;
      },
    };

    const colFatura = {
      Header: "Nº Fatura",
      accessor: "faturanumero",
    };

    const colImg = {
      Header: "",
      id: "img",
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ row }) => {
        const ref = String(row.original.refmaterial || "").trim();
        const url = imgsByRef?.[ref] || "";
        if (!url) return <span style={{ display: "inline-block", width: 32 }} />;

        return (
          <img
            src={url}
            alt={ref}
            title="Abrir imagem"
            style={{
              width: 26,
              height: 26,
              objectFit: "contain",
              borderRadius: 6,
              cursor: "pointer",
              border: "1px solid #eee",
              background: "#fff",
            }}
            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          />
        );
      },
    };

    const colMaterial = {
      Header: "Material",
      accessor: "nomematerial",
      Cell: ({ row }) => {
        const nome = row.original.nomematerial || "";
        const ref = row.original.refmaterial || "";
        return `${nome}${ref ? ` (${ref})` : ""}`;
      },
    };

    const colValor = {
      Header: "Valor Total",
      accessor: "valortotal",
      Cell: ({ value }) =>
        value != null
          ? Number(value).toLocaleString("pt-PT", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "",
    };

    const colLitros = {
      Header: "Litros",
      accessor: "volume",
      Cell: ({ value }) =>
        value != null
          ? Number(value).toLocaleString("pt-PT", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "",
    };

    const colPesoBru = {
      Header: "Peso Bruto",
      accessor: "pesobruto",
      Cell: ({ value }) =>
        value != null
          ? Number(value).toLocaleString("pt-PT", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "",
    };

    const colQtd = {
      Header: "Quantidade",
      accessor: "quantidade",
      Cell: ({ value }) =>
        value != null
          ? Number(value).toLocaleString("pt-PT", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "",
    };

    const metricCol =
      metric === "eur"
        ? colValor
        : metric === "litros"
        ? colLitros
        : metric === "qtd"
        ? colQtd
        : colPesoBru;

    const colCliente = {
      Header: "Cliente",
      accessor: "cliente",
      Cell: ({ row }) => {
        const nome = row.original.cliente;
        const nif = row.original.clientenif;

        if (nif && String(nif).trim() !== "") {
          return `${nome} (${nif})`;
        }

        return nome;
      },
    };

    const colVendedor = {
      Header: "Vendedor",
      accessor: "vendedor",
    };

    const colFabricante = {
      Header: "Fabricante",
      accessor: "fabricante",
    };

    const colFamilia = {
      Header: "Família",
      accessor: "familia",
    };

    return [
      colData,
      colFatura,
      colImg,
      colMaterial,
      metricCol,
      colCliente,
      colVendedor,
      colFabricante,
      colFamilia,
    ];
  }, [metric, imgsByRef]);

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data: rows,
      defaultColumn,
      initialState: {
        filters: savedFilters,
        sortBy: [
          {
            id: "faturadata",
            desc: true,
          },
        ],
      },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
    state: tableState,
  } = tableInstance;

  const columnsClientes = useMemo(
    () => [
      { Header: "Vendedor", accessor: "vendedor" },
      {
        Header: "Cliente",
        accessor: "cliente",
        Cell: ({ row }) => {
          const nome = row.original.cliente;
          const nif = row.original.clientenif;

          if (nif && String(nif).trim() !== "") {
            return `${nome} (${nif})`;
          }

          return nome;
        },
      },

      { Header: "Ref Cliente", accessor: "refcliente" },
      {
        Header: "€",
        accessor: "valortotal",
        Cell: ({ value }) =>
          value == null
            ? ""
            : Number(value).toLocaleString("pt-PT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
      },
      {
        Header: "Litros",
        accessor: "volume",
        Cell: ({ value }) =>
          value == null
            ? ""
            : Number(value).toLocaleString("pt-PT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
      },
      {
        Header: "Qtd",
        accessor: "quantidade",
        Cell: ({ value }) => (value == null ? "" : Number(value).toLocaleString("pt-PT")),
      },
    ],
    []
  );

  const tableClientesInstance = useTable(
    {
      columns: columnsClientes,
      data: clientesPorVendedor || [],
      defaultColumn,
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps: getTableClientesProps,
    getTableBodyProps: getTableClientesBodyProps,
    headerGroups: headerClientesGroups,
    rows: clientesTableRows,
    prepareRow: prepareClienteRow,
  } = tableClientesInstance;

  // Persistir filtros
  useEffect(() => {
    if (!keepFilters) return;
    try {
      const f = tableState.filters || [];
      localStorage.setItem(LS_FILTERS_KEY, JSON.stringify(f));
      localStorage.setItem(LS_KEEP_KEY, "1");
    } catch {}
  }, [tableState.filters, keepFilters]);

  useEffect(() => {
    if (!keepFilters) {
      try {
        localStorage.removeItem(LS_FILTERS_KEY);
        localStorage.setItem(LS_KEEP_KEY, "0");
      } catch {}
    }
  }, [keepFilters]);

  // ============================================================
  // Helpers de seleção (checkboxes)
  // ============================================================
  const allAnosSelected = anosSel.length === anosOptions.length;
  const toggleAllAnos = () => {
    if (allAnosSelected) setAnosSel([]);
    else setAnosSel([...anosOptions]);
  };
  const toggleAno = (ano) => {
    setAnosSel((prev) => (prev.includes(ano) ? prev.filter((a) => a !== ano) : [...prev, ano]));
  };

  const allVendRefs = vendedores.map((v) => v.ref);
  const allVendedoresSelected =
    allVendRefs.length > 0 && vendedoresSel.length === allVendRefs.length;

  const toggleAllVendedores = () => {
    if (vendedorForcado) return;

    setVendedoresSel(() => (allVendedoresSelected ? [] : allVendRefs));
  };

  const toggleVendedor = (ref) => {
    if (vendedorForcado) return;

    setVendedoresSel((prev) =>
      prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]
    );
  };

  const allFamRefs = familias.map((f) => f.ref);
  const allFamiliasSelected = allFamRefs.length > 0 && familiasSel.length === allFamRefs.length;

  const toggleAllFamilias = () => {
    setFamiliasSel(() => (allFamiliasSelected ? [] : allFamRefs));
  };

  const toggleFamilia = (ref) => {
    setFamiliasSel((prev) => (prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]));
  };

  // Só habilita o accordion de Materiais quando Famílias estiverem parcialmente selecionadas:
  // - NÃO pode ser 0 (nenhuma)
  // - NÃO podem ser todas
  const familiasParciais =
    familias.length > 0 && familiasSel.length > 0 && familiasSel.length < familias.length;

  useEffect(() => {
    // Se famílias NÃO estão parciais, desativa Materiais e limpa seleção/lista
    if (!familiasParciais) {
      setMateriaisDisabled(true);
      if (expandedPanel === "materiais") setExpandedPanel(null);
      setMateriais([]);
      setMateriaisSel([]);
      setMateriaisLoadedKey("");
      setMateriaisLoading(false);
      return;
    }

    // Se famílias estão parciais, habilita Materiais
    setMateriaisDisabled(false);

    // Key para evitar recarregar quando a seleção é igual (mesmo conjunto)
    const key = `${fabricanteSel?.ref || ""}::${[...familiasSel].sort().join("|")}`;

    if (!key) return;

    // Se já carregámos para esta key, não faz novo fetch
    if (materiaisLoadedKey === key) return;

    const carregarMateriais = async () => {
      try {
        setMateriaisLoading(true);

        const [dados, err] = await handleApi(
          api.post("Comercial/SelMateriaisCombo", {
            familias: familiasSel,
            refFabricante: fabricanteSel?.ref || "",
          })
        );

        if (err) {
          showAlert(err, "error");
          setMateriais([]);
          setMateriaisSel([]);
          setMateriaisLoadedKey("");
          return;
        }

        const lista = Array.isArray(dados) ? dados : [];

        setMateriais(lista);
        setMateriaisSel(lista.map((m) => m.ref));
        setMateriaisFiltro("");
        setMateriaisLoadedKey(key);
      } catch (e) {
        showAlert("Erro ao carregar materiais.", "error");
        setMateriais([]);
        setMateriaisSel([]);
        setMateriaisFiltro("");
        setMateriaisLoadedKey("");
      } finally {
        setMateriaisLoading(false);
      }
    };

    carregarMateriais();
  }, [familiasParciais, familiasSel, materiaisLoadedKey, expandedPanel, fabricanteSel]);

  // ----------------- Materiais -----------------
  const materiaisVisiveis = useMemo(() => {
    const f = (materiaisFiltro || "").trim().toLowerCase();
    if (!f) return materiais;

    return materiais.filter((m) => {
      const nome = (m.nome || "").toString().toLowerCase();
      const ref = (m.ref || "").toString().toLowerCase();
      return nome.includes(f) || ref.includes(f);
    });
  }, [materiais, materiaisFiltro]);

  const allMatRefs = materiais.map((m) => m.ref);
  const visiveisMatRefs = materiaisVisiveis.map((m) => m.ref);

  const allMateriaisSelected =
    visiveisMatRefs.length > 0 && visiveisMatRefs.every((r) => materiaisSel.includes(r));

  const toggleAllMateriais = () => {
    setMateriaisSel((prev) => {
      if (visiveisMatRefs.length === 0) return prev;

      // se já estão todos os visíveis selecionados, remove só os visíveis
      if (allMateriaisSelected) {
        return prev.filter((r) => !visiveisMatRefs.includes(r));
      }

      // caso contrário, adiciona os visíveis (sem duplicar)
      const setPrev = new Set(prev);
      visiveisMatRefs.forEach((r) => setPrev.add(r));
      return Array.from(setPrev);
    });
  };

  const toggleMaterial = (ref) => {
    setMateriaisSel((prev) =>
      prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]
    );
  };

  // Embalagens
  const allEmbRefs = embalagens.map((e) => e.ref);
  const allEmbalagensSelected = allEmbRefs.length > 0 && embalagensSel.length === allEmbRefs.length;

  const toggleAllEmbalagens = () => {
    setEmbalagensSel(() => (allEmbalagensSelected ? [] : allEmbRefs));
  };

  const toggleEmbalagem = (ref) => {
    setEmbalagensSel((prev) =>
      prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]
    );
  };

  // ============================================================
  // Render – filtros topo
  // ============================================================
  const filtrosTopo = (
    <MDBox px={2} py={2} display="flex" flexDirection="column" gap={1.75}>
      {/* Linha datas / fabricante / cliente / manter filtros / botão */}
      <MDBox display="flex" flexWrap="wrap" alignItems="flex-end" columnGap={2} rowGap={1.5}>
        {/* Mês inicial */}
        <MDBox sx={{ minWidth: 130 }}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
            Mês inicial
          </MDTypography>
          <TextField
            select
            size="small"
            value={mesIni}
            onChange={(e) => setMesIni(Number(e.target.value))}
            fullWidth
            SelectProps={{ native: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
              },
            }}
          >
            {MESES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </TextField>
        </MDBox>

        {/* Dia inicial */}
        <MDBox sx={{ minWidth: 90 }}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
            Dia inicial
          </MDTypography>
          <TextField
            select
            size="small"
            value={diaIni}
            onChange={(e) => setDiaIni(Number(e.target.value))}
            fullWidth
            SelectProps={{ native: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
              },
            }}
          >
            {DIAS.map((d) => (
              <option key={d} value={d}>
                {d.toString().padStart(2, "0")}
              </option>
            ))}
          </TextField>
        </MDBox>

        {/* Mês final */}
        <MDBox sx={{ minWidth: 130 }}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
            Mês final
          </MDTypography>
          <TextField
            select
            size="small"
            value={mesFim}
            onChange={(e) => setMesFim(Number(e.target.value))}
            fullWidth
            SelectProps={{ native: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
              },
            }}
          >
            {MESES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </TextField>
        </MDBox>

        {/* Dia final */}
        <MDBox sx={{ minWidth: 90 }}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
            Dia final
          </MDTypography>
          <TextField
            select
            size="small"
            value={diaFim}
            onChange={(e) => setDiaFim(Number(e.target.value))}
            fullWidth
            SelectProps={{ native: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
              },
            }}
          >
            {DIAS.map((d) => (
              <option key={d} value={d}>
                {d.toString().padStart(2, "0")}
              </option>
            ))}
          </TextField>
        </MDBox>

        {/* Fabricante */}
        <MDBox sx={{ minWidth: 220 }}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
            Fabricante
          </MDTypography>
          <Autocomplete
            size="small"
            options={fabricantes}
            value={fabricanteSel}
            onChange={(_, value) => setFabricanteSel(value)}
            getOptionLabel={(opt) =>
              opt
                ? `${opt.nome || opt.descricao || opt.designacao || ""}${
                    opt.ref ? ` (${opt.ref})` : ""
                  }`
                : ""
            }
            isOptionEqualToValue={(opt, val) => String(opt?.ref || "") === String(val?.ref || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="TODOS"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              />
            )}
          />
        </MDBox>

        {/* Cliente */}
        <MDBox sx={{ minWidth: 260 }}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
            Cliente
          </MDTypography>
          <Autocomplete
            size="small"
            options={clientes}
            value={clienteSel}
            onChange={(_, value) => setClienteSel(value)}
            getOptionLabel={(opt) =>
              opt ? `${opt.nome || opt.designacao || ""}${opt.ref ? ` (${opt.ref})` : ""}` : ""
            }
            isOptionEqualToValue={(opt, val) => String(opt?.ref || "") === String(val?.ref || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="TODOS"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              />
            )}
          />
        </MDBox>
      </MDBox>

      {/* Zona Anos */}
      <Accordion
        expanded={expandedPanel === "anos"}
        onChange={handleAccordionChange("anos")}
        disableGutters
        sx={{
          backgroundColor: "#fff7f0",
          borderRadius: 2,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 32,
            px: 1.5,
            "& .MuiAccordionSummary-content": { margin: "4px 0" },
          }}
        >
          <MDTypography variant="button" sx={{ fontSize: 12 }}>
            Anos
          </MDTypography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
          <MDBox mb={0.5}>
            <FormControlLabel
              control={<Checkbox size="small" checked={allAnosSelected} onChange={toggleAllAnos} />}
              label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todos</span>}
            />
          </MDBox>

          <MDBox display="flex" flexWrap="wrap" gap={1}>
            {anosOptions.map((ano) => (
              <FormControlLabel
                key={ano}
                control={
                  <Checkbox
                    size="small"
                    checked={anosSel.includes(ano)}
                    onChange={() => toggleAno(ano)}
                  />
                }
                label={<span style={{ fontSize: 13 }}>{ano}</span>}
                sx={{ mr: 2 }}
              />
            ))}
          </MDBox>
        </AccordionDetails>
      </Accordion>

      {/* Zona Vendedores */}
      {!vendedorForcado && (
        <Accordion
          expanded={expandedPanel === "vendedores"}
          onChange={handleAccordionChange("vendedores")}
          disableGutters
          disabled={!!vendedorForcado} // Comercial: accordion inativo
          sx={{
            backgroundColor: "#fff7f0",
            borderRadius: 2,
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 32,
              px: 1.5,
              "& .MuiAccordionSummary-content": { margin: "4px 0" },
            }}
          >
            <MDTypography variant="button" sx={{ fontSize: 12 }}>
              Vendedores
            </MDTypography>
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
            <MDBox mb={0.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allVendedoresSelected}
                    onChange={toggleAllVendedores}
                    disabled={!!vendedorForcado}
                  />
                }
                label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todos</span>}
              />
            </MDBox>

            <MDBox display="flex" flexWrap="wrap" gap={1}>
              {vendedores.map((v) => (
                <MDBox key={v.ref} sx={{ minWidth: 220 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={vendedoresSel.includes(v.ref)}
                        onChange={() => toggleVendedor(v.ref)}
                        disabled={!!vendedorForcado}
                      />
                    }
                    label={<span style={{ fontSize: 13 }}>{v.nome || v.designacao || v.ref}</span>}
                  />
                </MDBox>
              ))}
            </MDBox>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Zona Famílias */}
      <Accordion
        expanded={expandedPanel === "familias"}
        onChange={handleAccordionChange("familias")}
        disableGutters
        sx={{
          backgroundColor: "#fff7f0",
          borderRadius: 2,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 32,
            px: 1.5,
            "& .MuiAccordionSummary-content": { margin: "4px 0" },
          }}
        >
          <MDTypography variant="button" sx={{ fontSize: 12 }}>
            Famílias de materiais
          </MDTypography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
          <MDBox mb={0.5}>
            <FormControlLabel
              control={
                <Checkbox size="small" checked={allFamiliasSelected} onChange={toggleAllFamilias} />
              }
              label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todas</span>}
            />
          </MDBox>

          <MDBox display="flex" flexWrap="wrap" gap={1}>
            {familias.map((f) => (
              <MDBox key={f.ref} sx={{ minWidth: 260 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={familiasSel.includes(f.ref)}
                      onChange={() => toggleFamilia(f.ref)}
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>{f.nome || f.designacao || f.ref}</span>}
                />
              </MDBox>
            ))}
          </MDBox>
        </AccordionDetails>
      </Accordion>

      {/* Zona Materiais */}
      <Accordion
        expanded={expandedPanel === "materiais"}
        onChange={handleAccordionChange("materiais")}
        disableGutters
        disabled={materiaisDisabled}
        sx={{
          backgroundColor: "#fff7f0",
          borderRadius: 2,
          "&:before": { display: "none" },
          opacity: materiaisDisabled ? 0.65 : 1,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 32,
            px: 1.5,
            "& .MuiAccordionSummary-content": { margin: "4px 0" },
          }}
        >
          <MDTypography variant="button" sx={{ fontSize: 12 }}>
            Materiais
          </MDTypography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
          {materiaisDisabled ? (
            <MDTypography variant="caption" color="text">
              Para escolher materiais, selecione apenas algumas famílias (não todas / não nenhuma).
            </MDTypography>
          ) : materiaisLoading ? (
            <MDBox py={1} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} />
              <MDTypography variant="caption" color="text">
                A carregar materiais…
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <MDBox mb={1}>
                <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 0.5 }}>
                  Filtrar materiais
                </MDTypography>

                <TextField
                  value={materiaisFiltro}
                  onChange={(e) => setMateriaisFiltro(e.target.value)}
                  placeholder="Escreva para filtrar por nome ou ref…"
                  size="small"
                  fullWidth
                />
              </MDBox>
              <MDBox mb={0.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allMateriaisSelected}
                      onChange={toggleAllMateriais}
                    />
                  }
                  label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todos</span>}
                />
              </MDBox>

              <MDBox
                sx={{
                  maxHeight: 220, // ~ 4 linhas (ajusta se necessário)
                  overflowY: "auto",
                  pr: 1,
                }}
              >
                <MDBox display="flex" flexWrap="wrap" gap={1}>
                  {materiaisVisiveis.map((m) => (
                    <MDBox key={m.ref} sx={{ minWidth: 260 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={materiaisSel.includes(m.ref)}
                            onChange={() => toggleMaterial(m.ref)}
                          />
                        }
                        label={<span style={{ fontSize: 13 }}>{m.nome || m.ref}</span>}
                      />
                    </MDBox>
                  ))}

                  {materiaisVisiveis.length === 0 && (
                    <MDTypography variant="caption" color="text">
                      Sem resultados para os filtros indicados.
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Zona Embalagens */}
      <Accordion
        expanded={expandedPanel === "embalagens"}
        onChange={handleAccordionChange("embalagens")}
        disableGutters
        sx={{
          backgroundColor: "#fff7f0",
          borderRadius: 2,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 32,
            px: 1.5,
            "& .MuiAccordionSummary-content": { margin: "4px 0" },
          }}
        >
          <MDTypography variant="button" sx={{ fontSize: 12 }}>
            Embalagens
          </MDTypography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
          <MDBox mb={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={allEmbalagensSelected}
                  onChange={toggleAllEmbalagens}
                />
              }
              label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todas</span>}
            />
          </MDBox>

          <MDBox display="flex" flexWrap="wrap" gap={1}>
            {embalagens.map((e) => (
              <MDBox key={e.ref} sx={{ minWidth: 220 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={embalagensSel.includes(e.ref)}
                      onChange={() => toggleEmbalagem(e.ref)}
                    />
                  }
                  // mostra o NOME; a ref só serve de chave / fallback
                  label={<span style={{ fontSize: 13 }}>{e.nome || e.designacao || e.ref}</span>}
                />
              </MDBox>
            ))}
          </MDBox>
        </AccordionDetails>
      </Accordion>

      {/* Filtro por nome do material */}
      <MDBox mt={1} display="flex" flexWrap="wrap" alignItems="flex-end" columnGap={2} rowGap={1.5}>
        <MDBox>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
            Nome do material
          </MDTypography>
          <RadioGroup
            row
            value={nomeFiltroModo}
            onChange={(e) => setNomeFiltroModo(e.target.value)}
            sx={{ "& .MuiTypography-root": { fontSize: 12 } }}
          >
            <FormControlLabel value="contém" control={<Radio size="small" />} label="Contém" />
            <FormControlLabel
              value="naoContem"
              control={<Radio size="small" />}
              label="Não contém"
            />
          </RadioGroup>
        </MDBox>

        <MDBox sx={{ minWidth: 260 }}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
            Texto a procurar
          </MDTypography>
          <TextField
            size="small"
            value={nomeFiltroTexto}
            onChange={(e) => setNomeFiltroTexto(e.target.value)}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
              },
            }}
          />
        </MDBox>
      </MDBox>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <MDBox pt={1}>
        <Card>
          {/* Filtros */}
          {filtrosTopo}

          {/* Botão de atualização manual */}
          <MDBox sx={{ mb: 2, mr: 2, ml: "auto", minWidth: 160 }}>
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              disabled={
                loading ||
                vendedores.length === 0 ||
                fabricantes.length === 0 ||
                clientes.length === 0 ||
                familias.length === 0 ||
                embalagens.length === 0
              }
              onClick={() => carregarDados()}
            >
              Atualizar análise
            </MDButton>
          </MDBox>

          {/* Chart – MESMO MODELO do Dashboard/MenuSearchMateriais */}
          <MDBox px={2} pb={2}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <MDTypography variant="h6">{METRIC_CONFIG[metric].title}</MDTypography>

              <RadioGroup
                row
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                sx={{ "& .MuiTypography-root": { fontSize: 12 } }}
              >
                <FormControlLabel value="eur" control={<Radio size="small" />} label="Euros" />
                <FormControlLabel value="litros" control={<Radio size="small" />} label="Litros" />
                <FormControlLabel
                  value="qtd"
                  control={<Radio size="small" />}
                  label="Quantidades"
                />
                <FormControlLabel
                  value="clientes"
                  control={<Radio size="small" />}
                  label="Clientes"
                />

                {/* <FormControlLabel
                  value="pesoBru"
                  control={<Radio size="small" />}
                  label="Peso Bruto"
                /> */}
              </RadioGroup>
            </MDBox>

            <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
              {METRIC_CONFIG[metric].caption}
            </MDTypography>

            {loading ? (
              <MDBox py={4} display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </MDBox>
            ) : !vendasChart ? (
              <MDBox py={4} display="flex" justifyContent="center">
                <MDTypography variant="button" color="text">
                  Sem dados com os filtros selecionados.
                </MDTypography>
              </MDBox>
            ) : (
              <>
                <MDBox mb={1.5}>
                  <DefaultLineChart
                    chart={{
                      labels: vendasChart.labels,
                      datasets: vendasChart.datasets,
                    }}
                    height="16rem"
                  />
                </MDBox>

                {/* Legenda equivalente ao Dashboard */}
                <MDBox display="flex" flexWrap="wrap" gap={2} mb={0.5}>
                  {vendasChart.datasets.map((ds) => {
                    const label = String(ds.label || "");
                    const ano = label.includes("_") ? label.split("_")[0] : label;
                    const isCompras = label.endsWith("_Compras");
                    const totalAno = isCompras
                      ? vendasChart.totaisComprasAno?.[ano] ?? 0
                      : vendasChart.totaisAno?.[ano] ?? 0;

                    const suffix = METRIC_CONFIG[metric].suffix;

                    return (
                      <MDTypography
                        key={ds.label}
                        variant="button"
                        color="text"
                        sx={{ fontSize: 12, display: "inline-flex", alignItems: "center" }}
                      >
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            marginRight: 6,
                            backgroundColor: colorMap[ds.color] || "#999",
                            display: "inline-block",
                          }}
                        />
                        {label}
                        <span style={{ marginLeft: 4 }}>
                          (
                          <strong>
                            {fmt.format(totalAno)}
                            {suffix}
                          </strong>
                          )
                        </span>
                      </MDTypography>
                    );
                  })}
                </MDBox>
              </>
            )}
          </MDBox>

          {loading && (
            <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </MDBox>
          )}

          <MDBox px={2} pb={2} display="flex" alignItems="center" justifyContent="space-between">
            {/* Manter filtros da tabela */}

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={keepFilters}
                  onChange={(e) => setKeepFilters(e.target.checked)}
                />
              }
              label={<span style={{ fontSize: 12 }}>Manter filtros da tabela</span>}
            />
          </MDBox>

          {metric !== "clientes" && (
            <>
              {/* Tabela com faturação */}
              <MDBox px={2} pb={2}>
                <MDTypography variant="h6" gutterBottom>
                  Tabela de faturação de vendas
                </MDTypography>

                <MDBox sx={{ height: "50vh", overflow: "auto", borderRadius: 1 }}>
                  <table
                    {...getTableProps()}
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      {headerGroups.map((hg) => (
                        <tr {...hg.getHeaderGroupProps()}>
                          {hg.headers.map((col) => (
                            <th
                              {...col.getHeaderProps(col.getSortByToggleProps())}
                              style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                                background: "#fff",
                                textAlign: "left",
                                padding: "6px 8px",
                                fontSize: 12,
                                color: "#7b809a",
                                verticalAlign: "bottom",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                              }}
                            >
                              <MuiBox display="flex" alignItems="center">
                                {col.render("Header")}
                              </MuiBox>

                              {!col.disableFilters && (
                                <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                                  {col.canFilter ? col.render("Filter") : null}
                                </div>
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {tableRows.map((row) => {
                        prepareRow(row);
                        return (
                          <tr
                            {...row.getRowProps()}
                            style={{
                              borderBottom: "1px solid #f0f0f0",
                              transition: "background-color 0.15s",
                            }}
                          >
                            {row.cells.map((cell) => (
                              <td
                                {...cell.getCellProps()}
                                style={{
                                  padding: "6px 8px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })}

                      {tableRows.length === 0 && (
                        <tr>
                          <td
                            colSpan={columns.length}
                            style={{
                              padding: "12px 8px",
                              textAlign: "center",
                              fontSize: 12,
                              color: "#9e9e9e",
                            }}
                          >
                            Sem dados com os filtros selecionados.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </MDBox>
              </MDBox>
            </>
          )}

          {metric === "clientes" && (
            <MDBox px={2} pb={2}>
              <MDTypography variant="h6" gutterBottom>
                Clientes por vendedor ({clientesTableRows.length} distintos)
              </MDTypography>

              <Card sx={{ overflow: "hidden" }}>
                <MDBox sx={{ height: "50vh", overflow: "auto", borderRadius: 1 }}>
                  <table
                    {...getTableClientesProps()}
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      {headerClientesGroups.map((hg) => (
                        <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                          {hg.headers.map((col) => (
                            <th
                              {...col.getHeaderProps(col.getSortByToggleProps())}
                              key={col.id}
                              style={{
                                textAlign: "left",
                                padding: "10px 8px",
                                fontSize: 12,
                                fontWeight: 600,
                                borderBottom: "1px solid rgba(0,0,0,0.08)",
                                whiteSpace: "nowrap",
                                background: "#fff",
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                              }}
                            >
                              {col.render("Header")}
                              <div>{col.canFilter ? col.render("Filter") : null}</div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>

                    <tbody {...getTableClientesBodyProps()}>
                      {clientesTableRows.map((row) => {
                        prepareClienteRow(row);
                        return (
                          <tr
                            {...row.getRowProps()}
                            key={row.id}
                            style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
                          >
                            {row.cells.map((cell) => (
                              <td
                                {...cell.getCellProps()}
                                key={cell.column.id}
                                style={{
                                  padding: "8px",
                                  fontSize: 12,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })}

                      {clientesTableRows.length === 0 && (
                        <tr>
                          <td
                            colSpan={columnsClientes.length}
                            style={{ padding: 12, fontSize: 12 }}
                          >
                            Sem clientes a apresentar.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </MDBox>
              </Card>
            </MDBox>
          )}
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default AnaliseVendas;
